import React, {memo, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ForecastDaysMode} from '@/app/constants/forecast';
import {AnyRouteResult} from '@/app/interfaces/routing';
import {usePageMetaSetter} from '@/common/services/headTagger';
import {useRoute} from '@/common/services/router';
import {selectDomainConfig} from '@/app/store/config/selectors';
import {selectForecastData, selectForecastViewParams} from '@/app/store/forecast/selectors';
import {addRecentLocationAction} from '@/app/store/locationsHistory/actions';
import {AdBanner} from '@/app/components/common/advertising';
import {WeatherLocationSchemaMarkup} from '@/app/components/common/WeatherLocationSchemaMarkup';
import {WeatherPageHiddenHeader} from '@/app/components/common/WeatherPageHiddenHeader';
import {LocationBreadcrumbs} from '@/app/components/desktop/common/LocationBreadcrumbs';
import {useGetPageSeoMeta} from "@/app/hooks/useGetPageSeoMeta";
import {useWeatherActualize} from '@/app/hooks/useWeatherActualize';
import {WeatherDaily} from '../daily/WeatherDaily';
import {WeatherHeader} from '../WeatherHeader';
import {WeatherInformer} from '../WeatherInformer';
import {WeatherLinking} from '../WeatherLinking';
import {WeatherResorts} from '../WeatherResorts';
import {WeatherTopLocations} from '../WeatherTopLocations';
import styles from './Weather.css';

const extractFooterSideBanner = (input: unknown | unknown[]) => (Array.isArray(input) ? input[1] : input);

export const Weather = memo(() => {
	const {location, forecast = [], extra} = useSelector(selectForecastData)!;
	const {selectedDay, daysMode} = useSelector(selectForecastViewParams);
	const {domainName} = useSelector(selectDomainConfig);
	const {route, urlGenerator} = useRoute<AnyRouteResult>();
	const pageSeoMeta = useGetPageSeoMeta();

	// Set SEO tags
	usePageMetaSetter({
		...pageSeoMeta,
		canonicalUrl: `https://${domainName}${urlGenerator(route.name, route.params)}`.replace(/\/+$/, ''),
	});

	const dispatch = useDispatch();

	// Report about location view and scroll top
	useEffect(() => {
		dispatch(addRecentLocationAction({location}));
		window.scrollTo(0, 0);
	}, [location.id]);

	useWeatherActualize(location);

	return (
		<>
			<WeatherLocationSchemaMarkup location={location} />

			<WeatherPageHiddenHeader />
			<WeatherHeader location={location} selectedDaysMode={daysMode} isMainPage={extra.type === 'mainPage'} />

			<div className={styles.content}>
				<main className={styles.main}>
					{(daysMode === ForecastDaysMode.Days7 || daysMode === ForecastDaysMode.Days10) && (
						<WeatherDaily
							location={location}
							daysMode={daysMode}
							days={forecast}
							selectedDay={selectedDay}
						/>
					)}

					<div className={styles.footer}>
						<AdBanner
							className={styles.footerBottomAdBar}
							interfaceName='desktop'
							zoneName='weatherBottom'
							placeholder
						/>

						{extra.type === 'mainPage' && <WeatherTopLocations locations={extra.topLocationsWeather} />}

						{extra.type === 'settlementPage' && (
							<WeatherLinking
								locationSuperior={location.superior}
								regionTopLocations={extra.regionTopLocationWeather}
								countryTopLocations={extra.countryTopLocations}
							/>
						)}

						<WeatherInformer />

						{extra.type === 'settlementPage' && (
							<LocationBreadcrumbs location={location} superiorLocations={location.superior} />
						)}

						{daysMode === ForecastDaysMode.Days10 && (
							<AdBanner
								className={styles.footerSideAdBar}
								interfaceName='desktop'
								zoneName='weatherSide'
								zoneDataReducer={extractFooterSideBanner}
								placeholder
							/>
						)}
					</div>
				</main>

				{daysMode !== ForecastDaysMode.Days10 && (
					<aside className={styles.sideAdBar}>
						<AdBanner
							className={styles.sideAdBarWrapper}
							entryClassName={styles.sideAdBarEntry}
							interfaceName='desktop'
							zoneName='weatherSide'
							placeholder
						/>

						{extra.type === 'mainPage' && (
							<WeatherResorts
								period={extra.resortsWeather.period}
								locations={extra.resortsWeather.locations}
							/>
						)}
					</aside>
				)}
			</div>
		</>
	);
});

Weather.displayName = 'Weather';
